import React from "react";
import { Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import classNames from "classnames";

import GameActionButtons from "./GameActionButtons";

import { selectSelectedTeam } from "../features/clubhouse/clubhouseSlice";
import { selectFields } from "../features/global/globalSlice";
import { selectSelectedTournament } from "../features/tourneymgmt/tournamentManagementSlice";
import { useAppSelector } from "../hooks/reduxHooks";
import useBreakpoint from "../hooks/useBreakpoint";
import { TEAM_LOGO_URL } from "../lib/globals";
import { canEditTeam, isXOrBigger, isXOrSmaller } from "../lib/utils";
import { IGame, ISiteSetting } from "../types/api";

const Schedule = ({
    schedule,
    displayDeleteLink,
    setSchedule,
    scheduleHeader,
    scheduleType,
    showButtons = false,
    ageLevelGameIds,
}: {
    schedule: IGame[] | null;
    displayDeleteLink?: ISiteSetting;
    setSchedule?: React.Dispatch<React.SetStateAction<IGame[]>>;
    scheduleHeader?: string;
    scheduleType: "team" | "tournament";
    showButtons?: boolean;
    ageLevelGameIds?: Record<string, number[]>;
}): React.JSX.Element => {
    let newMonth: string;
    let newDay: string;
    const selectedTeam = useAppSelector(selectSelectedTeam);
    const selectedTournament = useAppSelector(selectSelectedTournament);
    const fields = useAppSelector(selectFields);

    const breakpoint = useBreakpoint();

    const handleDelete = (gameId: number) => {
        const newSchedule = schedule!.filter(g => g.id !== gameId);

        setSchedule && setSchedule(newSchedule);
    };

    const timeAndLocation = (s: IGame, gameTime: string) => (
        <td
            className="timeAndLocation"
            width={isXOrSmaller("md", breakpoint) ? undefined : "20%"}
            rowSpan={isXOrBigger("lg", breakpoint) ? 2 : 1}
            colSpan={isXOrSmaller("md", breakpoint) ? 7 : undefined}
        >
            {s.isPlayoff ? (
                <span className={classNames("noPrint")}>
                    <b>
                        {s.champGameInfo
                            ? "Championship Play"
                            : "Consolation Play"}
                    </b>
                    <br />
                </span>
            ) : (
                <span className={classNames("noPrint")}>
                    <b>Pool Play</b>
                    <br />
                </span>
            )}
            {ageLevelGameIds
                ? `${gameTime} - ${Object.entries(ageLevelGameIds).find(([a, g]) => g.includes(s.id))?.[0]}`
                : gameTime}
            <span className={classNames("noPrint")}>
                <br />@{" "}
                <Link to={`/fields/${s.fieldId}`} target="_blank">
                    {s.fieldName}
                </Link>
            </span>
            {s.diamond ? (
                <>
                    <br />
                    Diamond:{" "}
                    {
                        fields
                            .find(f => f.id === s.fieldId)
                            ?.diamonds.find(d => d.id === s.diamond)?.name
                    }
                </>
            ) : (
                ""
            )}
            {s.note ? (
                <>
                    <br />
                    Field Note: {s.note}
                </>
            ) : (
                ""
            )}
        </td>
    );

    const teamScoreInfo = (s: IGame) => (
        <>
            <td className="visitor logo" width={"8%"}>
                {s.visitor.logo && (
                    <img
                        src={`${TEAM_LOGO_URL}${s.visitor.logo}`}
                        alt={`${s.visitor.name} logo`}
                        className="teamLogo"
                    />
                )}
            </td>
            <td className="visitor team" width={"20%"}>
                {s.visitor.id
                    ? s.visitor.name
                    : s.isPlayoff
                      ? s.champGameInfo
                          ? s.champGameInfo?.visitor.seed
                              ? `Seed #${s.champGameInfo?.visitor.seed}`
                              : `Winner of Game #${s.champGameInfo?.visitor.winnerOf}`
                          : s.consGameInfo?.visitor.seed
                            ? `Seed #${s.consGameInfo?.visitor.seed}`
                            : `Winner of Game #${s.consGameInfo?.visitor.winnerOf}`
                      : "TBD"}
            </td>
            <td className="visitor score" width={"8%"}>
                {s.visitor.score}
            </td>
            <td className="at" width={"8%"}>
                @
            </td>
            <td className="home logo" width={"8%"}>
                {s.home.logo && (
                    <img
                        src={`${TEAM_LOGO_URL}${s.home.logo}`}
                        alt={`${s.home.name} logo`}
                        className="teamLogo"
                    />
                )}
            </td>
            <td className="home team" width={"20%"}>
                {s.home.id
                    ? s.home.name
                    : s.isPlayoff
                      ? s.champGameInfo
                          ? s.champGameInfo?.home.seed
                              ? `Seed #${s.champGameInfo?.home.seed}`
                              : `Winner of Game #${s.champGameInfo?.home.winnerOf}`
                          : s.consGameInfo?.home.seed
                            ? `Seed #${s.consGameInfo?.home.seed}`
                            : `Winner of Game #${s.consGameInfo?.home.winnerOf}`
                      : "TBD"}
            </td>
            <td className="home score" width={"8%"}>
                {s.home.score}
            </td>
        </>
    );

    return (
        <div className="schedule">
            {scheduleHeader && <h2>{scheduleHeader}</h2>}
            {schedule &&
                schedule.map((s, idx) => {
                    let monthOut: React.ReactNode;
                    let dayOut: React.ReactNode;

                    const date = new Date(s.date);

                    const gameMonth =
                        date.toLocaleString("default", {
                            month: "long",
                        }) +
                        " " +
                        date.getFullYear();

                    const gameDate = new Intl.DateTimeFormat("en-US", {
                        dateStyle: "long",
                    }).format(date);

                    if (gameMonth != newMonth) {
                        newMonth = gameMonth;

                        monthOut = <h3>{gameMonth}</h3>;
                    }

                    if (gameDate != newDay) {
                        newDay = gameDate;

                        dayOut = <h4>{gameDate}</h4>;
                    }

                    const gameTime = new Intl.DateTimeFormat("en-US", {
                        timeStyle: "short",
                    }).format(date);

                    return (
                        <Row
                            key={`game-${idx}`}
                            className={classNames({ noPrint: s.isPlayoff })}
                        >
                            <Col>
                                <span className={classNames("noPrint")}>
                                    {monthOut}
                                </span>
                                <span className={classNames("noPrint")}>
                                    {dayOut}
                                </span>
                                <Table
                                    className="scheduleGame"
                                    size="sm"
                                    borderless
                                >
                                    <tbody>
                                        {isXOrSmaller("md", breakpoint) ? (
                                            <>
                                                <tr>
                                                    <td
                                                        colSpan={7}
                                                        className="bracketGame"
                                                    >
                                                        {s.tournamentName}
                                                        {s.isPlayoff ? (
                                                            <>
                                                                <br />
                                                                {s.champGameInfo
                                                                    ? "Championship"
                                                                    : "Consolation"}{" "}
                                                                Bracket Game #
                                                                {s.champGameInfo
                                                                    ? s
                                                                          .champGameInfo
                                                                          ?.playoffGameId
                                                                    : s
                                                                          .consGameInfo
                                                                          ?.playoffGameId}
                                                            </>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr
                                                    className={
                                                        s.isPlayoff
                                                            ? "playoffGame"
                                                            : undefined
                                                    }
                                                >
                                                    {timeAndLocation(
                                                        s,
                                                        gameTime,
                                                    )}
                                                </tr>
                                                <tr>{teamScoreInfo(s)}</tr>
                                            </>
                                        ) : (
                                            <>
                                                <tr>
                                                    {timeAndLocation(
                                                        s,
                                                        gameTime,
                                                    )}
                                                    <td
                                                        colSpan={7}
                                                        className="bracketGame"
                                                    >
                                                        <h4>
                                                            {s.tournamentName}
                                                        </h4>
                                                        {s.isPlayoff ? (
                                                            <>
                                                                {s.champGameInfo
                                                                    ? "Championship"
                                                                    : "Consolation"}{" "}
                                                                Bracket Game #
                                                                {s.champGameInfo
                                                                    ? s
                                                                          .champGameInfo
                                                                          ?.playoffGameId
                                                                    : s
                                                                          .consGameInfo
                                                                          ?.playoffGameId}
                                                            </>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>{teamScoreInfo(s)}</tr>
                                            </>
                                        )}
                                        {showButtons &&
                                            (selectedTournament ||
                                                (selectedTeam &&
                                                    canEditTeam(
                                                        selectedTeam,
                                                    ))) && (
                                                <GameActionButtons
                                                    game={s}
                                                    selectedTeam={selectedTeam}
                                                    selectedTournament={
                                                        selectedTournament
                                                    }
                                                    displayDeleteLink={
                                                        displayDeleteLink
                                                    }
                                                    handleDelete={handleDelete}
                                                    setSchedule={setSchedule!}
                                                    breakpoint={breakpoint}
                                                    scheduleType={scheduleType}
                                                />
                                            )}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    );
                })}
        </div>
    );
};

export default Schedule;
